import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import FloatingWhatsApp from '@/components/FloatingWhatsApp';
import {
  ArrowRight, Store, GraduationCap, CreditCard, Shield, BarChart3, Zap, Users, Globe,
  ShoppingCart, BookOpen, Wallet, Heart, Truck, Tag, FileText, Headphones, Mail,
  CheckCircle2, Star, ChevronDown, ChevronUp, Menu, X, Play, Sparkles, TrendingUp,
  Lock, Smartphone, Layers, Award, MessageCircle, Clock, ArrowUpRight,
  Gift, Monitor, Megaphone, UserCheck, LayoutTemplate, Landmark, Package, Target,
  Repeat, BrainCircuit, BadgePercent, Receipt
} from 'lucide-react';

// Icon name → lucide component lookup. Mirror of the registry in admin LandingFeatures.js.
const FEATURE_ICON_REGISTRY = {
  Store, GraduationCap, Monitor, Shield, Gift, LayoutTemplate, UserCheck, Landmark,
  CreditCard, Zap, BarChart3, Users, Megaphone, Truck, Package, Globe,
  ShoppingCart, Wallet, Heart, Tag, FileText, Headphones, Mail, Sparkles,
  TrendingUp, Lock, Smartphone, Layers, Award, MessageCircle, Clock, ArrowUpRight,
  BookOpen, Target, Repeat, BadgePercent, Receipt,
};

// Fallback used while /api/landing-features hasn't responded (or fails).
const FALLBACK_FEATURES = [
  { icon: 'Store', title: 'Store Builder', description: 'Bangun toko online profesional dengan drag & drop builder dan template siap pakai', color: '#2D6CDF' },
  { icon: 'GraduationCap', title: 'LMS & Sertifikat', description: 'Kursus online dengan video, quiz, progress tracking, dan sertifikat otomatis yang bisa diverifikasi', color: '#8B5CF6' },
  { icon: 'Monitor', title: 'POS / Kasir', description: 'Sistem kasir lengkap untuk toko offline — manajemen shift, struk, dan laporan POS real-time', color: '#059669' },
  { icon: 'Shield', title: 'Membership Premium', description: 'Komunitas eksklusif dengan level keanggotaan, konten terkunci, dan recurring payment', color: '#10B981' },
];

const stats = [
  { value: '1,000+', label: 'Toko Aktif' },
  { value: '500K+', label: 'Transaksi/Bulan' },
  { value: '50+', label: 'Modul & Fitur' },
  { value: '99.9%', label: 'Uptime Server' },
];

// Fallback labels — replaced at runtime by /api/features registry (single source of truth)
const FALLBACK_FEATURE_LABELS = {
  physical: 'Produk Fisik',
  digital: 'Produk Digital',
  serial_key: 'Serial Key Digital',
  course: 'LMS & Kursus Online',
  membership: 'Membership Premium',
  donation: 'Donasi & Crowdfunding',
  webinar: 'Webinar',
  event_ticket: 'Event & Tiket',
  booking: 'Booking & Janji Temu',
  ppob: 'PPOB & Pulsa',
  high_ticket: 'High Ticket Sales',
  payment_plan: 'Payment Plan & Cicilan',
  pos: 'POS Kasir',
  checkout: 'Checkout Lengkap',
  affiliate: 'Affiliate Multi-Level',
  reseller: 'Reseller / Dropship',
  vouchers: 'Voucher & Promo',
  upsell: 'Order Bump & Upsell',
  bundles: 'Bundling Produk',
  landing_builder: 'Landing Page Builder',
  broadcast: 'Broadcast Email/WA',
  marketing_automation: 'Follow-Up & Abandoned Cart',
  lead_capture: 'Lead Management',
  loyalty: 'Loyalty & Reward',
  segments: 'Segmentasi Pelanggan',
  page_builder: 'Page Builder (CMS)',
  blog: 'Blog & Konten',
  multi_staff: 'Multi Staff & Role',
  accounting: 'Akuntansi',
  import_export: 'Import / Export Data',
  basic_reports: 'Laporan Dasar',
  reports: 'Laporan & Analytics Lengkap',
  custom_domain: 'Custom Domain',
  api_access: 'API & Webhooks',
  white_label: 'Whitelabel Branding',
};

const PLAN_META = {
  Starter:      { desc: 'Cocok untuk pemula yang baru mulai bisnis online',           cta: 'Mulai Sekarang' },
  Business:     { desc: 'Untuk bisnis yang sedang berkembang dan butuh fitur lebih', cta: 'Coba Sekarang' },
  Bisnis:       { desc: 'Untuk bisnis yang sedang berkembang dan butuh fitur lebih', cta: 'Coba Sekarang' },
  Professional: { desc: 'Untuk bisnis yang sedang berkembang dan butuh fitur lebih', cta: 'Coba Sekarang' },
  Enterprise:   { desc: 'Solusi lengkap untuk bisnis skala besar dan tim',           cta: 'Hubungi Sales' },
};

function buildPlan(pkg, idx, total, period = 'monthly', labelMap = FALLBACK_FEATURE_LABELS, anyExplicitPopular = false, trialDays = 14) {
  const fmt = new Intl.NumberFormat('id-ID');
  // Prefer admin's explicit is_popular flag. If no package has it set, fall back to auto (middle paket).
  const isPopular = anyExplicitPopular
    ? !!pkg.is_popular
    : (total >= 2 && idx === Math.min(1, total - 1));
  const isYearly = period === 'yearly';
  const fallbackMeta = PLAN_META[pkg.name] || { desc: pkg.name, cta: 'Pilih Paket' };
  const meta = {
    desc: (pkg.description && pkg.description.trim()) || fallbackMeta.desc,
    cta: (pkg.cta_label && pkg.cta_label.trim()) || fallbackMeta.cta,
  };
  const quotas = [
    `Produk: ${pkg.max_products === -1 ? 'Unlimited' : fmt.format(pkg.max_products)}`,
    `Order: ${pkg.max_orders === -1 ? 'Unlimited' : fmt.format(pkg.max_orders)}`,
    `Customer: ${pkg.max_customers === -1 ? 'Unlimited' : fmt.format(pkg.max_customers)}`,
  ];
  const featureLabels = (pkg.features || []).map((f) => labelMap[f] || FALLBACK_FEATURE_LABELS[f] || f);
  const rawPrice = isYearly ? pkg.price_yearly : pkg.price_monthly;
  const isFree = !pkg.price_monthly && !pkg.price_yearly;
  let priceLabel, periodLabel, subPrice = null;
  if (isFree) {
    priceLabel = 'Gratis';
    periodLabel = trialDays > 0 ? `${trialDays} hari` : '';
  } else {
    priceLabel = `Rp ${fmt.format(rawPrice || 0)}`;
    periodLabel = isYearly ? '/tahun' : '/bulan';
    if (isYearly && pkg.price_yearly > 0) {
      const perMonth = Math.round(pkg.price_yearly / 12);
      subPrice = `≈ Rp ${fmt.format(perMonth)}/bulan`;
    }
  }
  return {
    name: pkg.name,
    price: priceLabel,
    period: periodLabel,
    subPrice,
    desc: meta.desc,
    badge: isPopular ? 'Populer' : null,
    features: [...quotas, ...featureLabels],
    cta: meta.cta,
    variant: isPopular ? 'default' : 'outline',
  };
}

function calcYearlySavings(rawPlans) {
  const paid = rawPlans.find((p) => p.price_monthly > 0 && p.price_yearly > 0);
  if (!paid) return 0;
  const full = paid.price_monthly * 12;
  return full > 0 ? Math.round(((full - paid.price_yearly) / full) * 100) : 0;
}

// Fallback testimonials — replaced at runtime by /api/testimonials
const FALLBACK_TESTIMONIALS = [
  { name: 'Rina Sari', role: 'Owner, BatikNusantara.id', avatar: 'RS', text: 'Sejak pakai Tokomoe, penjualan batik online saya naik 3x lipat.', rating: 5 },
  { name: 'Ahmad Fauzi', role: 'Founder, KelasDigital.co', avatar: 'AF', text: 'Platform LMS-nya luar biasa, sertifikat otomatis bikin murid engaged.', rating: 5 },
];

const FALLBACK_FAQS = [
  { q: 'Apakah Tokomoe benar-benar gratis?', a: 'Ya! Paket Free gratis selama 14 hari dengan fitur dasar termasuk produk fisik, order, dan manajemen pelanggan. Upgrade ke paket berbayar untuk fitur premium seperti LMS, loyalty, dan sales funnel.' },
  { q: 'Apakah saya bisa menggunakan domain sendiri?', a: 'Tentu! Paket Business dan Enterprise mendukung custom domain dan whitelabel branding. Subdomain gratis otomatis tersedia untuk semua paket.' },
];

const howItWorks = [
  { step: '1', title: 'Daftar Akun', desc: 'Buat akun gratis dalam 30 detik. Tidak perlu kartu kredit.', icon: Sparkles },
  { step: '2', title: 'Setup Toko', desc: 'Pilih template, atur produk, dan kustomisasi tampilan toko Anda.', icon: Layers },
  { step: '3', title: 'Terima Pesanan', desc: 'Toko langsung aktif! Mulai terima pesanan dan kelola bisnis.', icon: TrendingUp },
];

export default function Landing() {
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [rawPlans, setRawPlans] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [faqs, setFaqs] = useState(FALLBACK_FAQS);
  const [featureLabelMap, setFeatureLabelMap] = useState(FALLBACK_FEATURE_LABELS);
  const [features, setFeatures] = useState(FALLBACK_FEATURES);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [ctaContent, setCtaContent] = useState({
    cta_title: 'Siap Mulai Bisnis Online Anda?',
    cta_subtitle: 'Bergabung dengan 10,000+ pebisnis Indonesia yang sudah menggunakan Tokomoe. Daftar gratis, tanpa kartu kredit.',
    cta_primary_btn: 'Daftar Gratis Sekarang',
    cta_secondary_btn: 'Lihat Harga',
    cta_features: ['Gratis 14 hari', 'Setup 30 detik', 'Cancel kapan saja'],
    trial_enabled: true,
    trial_days: 14,
  });

  useEffect(() => {
    api.get('/landing-content')
      .then((res) => { if (res.data) setCtaContent((p) => ({ ...p, ...res.data })); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/packages')
      .then((res) => {
        const pkgs = (res.data?.packages || [])
          .filter((p) => p.is_active !== false)
          .sort((a, b) => (a.price_monthly || 0) - (b.price_monthly || 0));
        setRawPlans(pkgs);
      })
      .catch(() => setRawPlans([]));

    api.get('/faqs')
      .then((res) => {
        const arr = res.data?.faqs || [];
        if (arr.length) setFaqs(arr.map((it) => ({ q: it.question, a: it.answer })));
      })
      .catch(() => {});

    api.get('/features')
      .then((res) => {
        const arr = res.data?.features || [];
        if (arr.length) {
          const map = Object.fromEntries(arr.map((f) => [f.code, f.label]));
          setFeatureLabelMap({ ...FALLBACK_FEATURE_LABELS, ...map });
        }
      })
      .catch(() => {});

    api.get('/landing-features')
      .then((res) => {
        const arr = res.data?.features || [];
        if (arr.length) setFeatures(arr);
      })
      .catch(() => {});

    api.get('/testimonials')
      .then((res) => {
        const arr = res.data?.testimonials || [];
        if (arr.length) setTestimonials(arr);
      })
      .catch(() => {});
  }, []);

  const anyExplicitPopular = rawPlans.some((p) => p.is_popular);
  const plans = rawPlans.map((p, i) => buildPlan(p, i, rawPlans.length, billingPeriod, featureLabelMap, anyExplicitPopular, ctaContent.trial_days));
  const yearlySavings = calcYearlySavings(rawPlans);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenu(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* ===== HEADER ===== */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/logo.png" alt="Tokomoe" className="h-9 object-contain" />
          </div>
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <button onClick={() => scrollTo('features')} className="hover:text-slate-900 transition-colors">Fitur</button>
            <button onClick={() => scrollTo('how-it-works')} className="hover:text-slate-900 transition-colors">Cara Kerja</button>
            <button onClick={() => scrollTo('pricing')} className="hover:text-slate-900 transition-colors">Harga</button>
            <button onClick={() => scrollTo('testimonials')} className="hover:text-slate-900 transition-colors">Testimoni</button>
            <button onClick={() => scrollTo('faq')} className="hover:text-slate-900 transition-colors">FAQ</button>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="landing-login-btn" className="text-slate-600 hover:text-slate-900">Masuk</Button>
            <Button onClick={() => navigate('/register')} data-testid="landing-register-btn" className="bg-[#2D6CDF] hover:bg-[#1A5BC4] text-white shadow-sm">Daftar Gratis</Button>
          </div>
          {/* Mobile hamburger */}
          <button className="md:hidden p-2" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {/* Mobile menu */}
        {mobileMenu && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 py-4 space-y-3">
            <button onClick={() => scrollTo('features')} className="block w-full text-left py-2 text-sm text-slate-600 hover:text-slate-900">Fitur</button>
            <button onClick={() => scrollTo('how-it-works')} className="block w-full text-left py-2 text-sm text-slate-600 hover:text-slate-900">Cara Kerja</button>
            <button onClick={() => scrollTo('pricing')} className="block w-full text-left py-2 text-sm text-slate-600 hover:text-slate-900">Harga</button>
            <button onClick={() => scrollTo('testimonials')} className="block w-full text-left py-2 text-sm text-slate-600 hover:text-slate-900">Testimoni</button>
            <button onClick={() => scrollTo('faq')} className="block w-full text-left py-2 text-sm text-slate-600 hover:text-slate-900">FAQ</button>
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/login')}>Masuk</Button>
              <Button className="flex-1 bg-[#2D6CDF] text-white" onClick={() => navigate('/register')}>Daftar</Button>
            </div>
          </div>
        )}
      </header>

      {/* ===== HERO ===== */}
      <section className="py-20 md:py-28 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% -20%, rgba(45,108,223,0.08) 0%, transparent 70%)' }} />
        <div className="absolute top-20 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-100/20 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#2D6CDF]/10 text-[#2D6CDF] rounded-full text-xs font-bold uppercase tracking-wider mb-6">
                <Zap size={14} /> Platform Toko Online UMKM #1 Indonesia
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Bangun Bisnis Online <span className="text-[#2D6CDF]">Lengkap</span> dalam Hitungan Menit
              </h1>
              <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
                Toko online, kursus, membership, POS kasir, loyalty program, reseller, sales funnel, dan 50+ fitur bisnis — semua dalam satu platform. Tanpa coding, langsung jalan.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Button size="lg" onClick={() => navigate('/register')} data-testid="hero-cta-btn" className="bg-[#2D6CDF] hover:bg-[#1A5BC4] text-white shadow-lg shadow-blue-500/25 h-12 px-8 text-base">
                  Mulai Gratis Sekarang <ArrowRight size={18} className="ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => scrollTo('how-it-works')} className="h-12 px-8 text-base border-slate-300 group">
                  <Play size={16} className="mr-2 text-[#2D6CDF] group-hover:text-[#1A5BC4]" /> Lihat Cara Kerja
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-slate-500">
                {ctaContent.trial_enabled && (
                  <span className="flex items-center gap-1.5"><Shield size={14} className="text-emerald-500" /> Gratis {ctaContent.trial_days} hari</span>
                )}
                <span className="flex items-center gap-1.5"><Globe size={14} className="text-[#2D6CDF]" /> Subdomain otomatis</span>
                <span className="flex items-center gap-1.5"><CreditCard size={14} className="text-amber-500" /> Tanpa kartu kredit</span>
              </div>
            </div>
            {/* Hero visual - Dashboard preview */}
            <div className="hidden lg:block relative">
              <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-200/60 border border-slate-200 p-6 transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="ml-2 text-xs text-slate-400 font-mono">dashboard.tokomoe.com</span>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 rounded-xl p-3"><p className="text-xs text-slate-500">Penjualan</p><p className="text-lg font-bold text-slate-900">Rp 12.5M</p><p className="text-xs text-emerald-600">+23%</p></div>
                    <div className="bg-emerald-50 rounded-xl p-3"><p className="text-xs text-slate-500">Pesanan</p><p className="text-lg font-bold text-slate-900">847</p><p className="text-xs text-emerald-600">+15%</p></div>
                    <div className="bg-amber-50 rounded-xl p-3"><p className="text-xs text-slate-500">Customer</p><p className="text-lg font-bold text-slate-900">2,341</p><p className="text-xs text-emerald-600">+31%</p></div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 h-32 flex items-end gap-1">
                    {[40,55,45,70,60,80,65,90,75,95,85,100].map((h,i) => (
                      <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: i >= 10 ? '#2D6CDF' : `rgba(15,82,255,${0.2 + i * 0.06})` }} />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center"><GraduationCap size={16} className="text-violet-600" /></div>
                      <div><p className="text-xs text-slate-500">Kursus Aktif</p><p className="text-sm font-semibold">12 Kursus</p></div>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center"><Users size={16} className="text-emerald-600" /></div>
                      <div><p className="text-xs text-slate-500">Member</p><p className="text-sm font-semibold">589 Aktif</p></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg border border-slate-100 p-3 flex items-center gap-3 animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center"><CheckCircle2 size={16} className="text-emerald-600" /></div>
                <div><p className="text-xs font-semibold text-slate-900">Pesanan Baru!</p><p className="text-[10px] text-slate-500">Rp 450,000 — 2 menit lalu</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF STATS ===== */}
      <section className="py-12 bg-slate-900">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <p className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>{s.value}</p>
                <p className="text-sm text-slate-400 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 px-3 py-1 text-[#2D6CDF] border-[#2D6CDF]/30 font-semibold">50+ Fitur Lengkap</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Semua yang Anda Butuhkan, Satu Platform
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Dari toko online, kursus digital, POS kasir, hingga loyalty program dan landing page builder — kelola seluruh bisnis dalam satu dashboard
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {features.map((f, i) => {
              const Icon = (typeof f.icon === 'string' ? FEATURE_ICON_REGISTRY[f.icon] : f.icon) || Sparkles;
              const title = f.title;
              const desc = f.description || f.desc;
              const color = f.color || '#2D6CDF';
              return (
              <div key={f.id || i} className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:shadow-lg hover:shadow-slate-200/50 hover:-translate-y-1 duration-300" data-testid={`feature-card-${i}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-colors" style={{ backgroundColor: `${color}15` }}>
                  <Icon size={22} style={{ color }} />
                </div>
                <h3 className="text-base font-semibold text-slate-900 tracking-tight">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 px-3 py-1 text-emerald-600 border-emerald-300 font-semibold">Mudah & Cepat</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              3 Langkah Mulai Bisnis Online
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl mx-auto">
              Tidak butuh skill teknis. Cukup daftar, setup, dan mulai terima pesanan.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {howItWorks.map(({ step, title, desc, icon: Icon }, i) => (
              <div key={i} className="relative text-center">
                {i < 2 && <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px border-t-2 border-dashed border-slate-300" />}
                <div className="w-20 h-20 rounded-2xl bg-white shadow-md shadow-slate-200/60 border border-slate-200 flex items-center justify-center mx-auto mb-5 relative">
                  <Icon size={32} className="text-[#2D6CDF]" />
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#2D6CDF] text-white text-xs font-bold flex items-center justify-center shadow">{step}</div>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SHOWCASE / PRODUCT TYPES ===== */}
      <section id="use-cases" className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 px-3 py-1 text-violet-600 border-violet-300 font-semibold">Multi-Bisnis</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Satu Platform, Banyak Jenis Bisnis
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Apapun model bisnis Anda, Tokomoe siap mendukung
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShoppingCart, title: 'Toko Produk Fisik', desc: 'Jual produk fisik dengan multi kurir, COD, tracking real-time, dan POS kasir offline', bg: 'bg-blue-50', iconColor: 'text-blue-600' },
              { icon: BookOpen, title: 'Kursus & E-Learning', desc: 'LMS lengkap dengan video, quiz, sertifikat otomatis, dan progress tracking', bg: 'bg-violet-50', iconColor: 'text-violet-600' },
              { icon: Lock, title: 'Membership & Subscription', desc: 'Komunitas eksklusif, konten premium, level keanggotaan, dan recurring payment', bg: 'bg-emerald-50', iconColor: 'text-emerald-600' },
              { icon: Wallet, title: 'High Ticket & Jasa', desc: 'Payment plan, DP & cicilan, kontrak digital, dan milestone tracking', bg: 'bg-amber-50', iconColor: 'text-amber-600' },
              { icon: Heart, title: 'Donasi & Crowdfunding', desc: 'Campaign donasi online dengan progress bar, transparansi, dan laporan', bg: 'bg-pink-50', iconColor: 'text-pink-600' },
              { icon: Smartphone, title: 'Produk Digital & PPOB', desc: 'E-book, template, pulsa, token PLN, voucher game, e-money, dan BPJS', bg: 'bg-orange-50', iconColor: 'text-orange-600' },
              { icon: Target, title: 'Reseller & Affiliate', desc: 'Bangun jaringan reseller multi-tier dan program affiliate dengan komisi otomatis', bg: 'bg-cyan-50', iconColor: 'text-cyan-600' },
              { icon: LayoutTemplate, title: 'Sales Funnel & LP', desc: 'Landing page builder, upsell/downsell, order bump, dan lead capture form', bg: 'bg-purple-50', iconColor: 'text-purple-600' },
            ].map(({ icon: Icon, title, desc, bg, iconColor }, i) => (
              <Card key={i} className="border-slate-200 hover:shadow-md transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center mb-4`}>
                    <Icon size={24} className={iconColor} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 px-3 py-1 text-amber-600 border-amber-300 font-semibold">Harga Transparan</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Pilih Paket yang Sesuai Bisnis Anda
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl mx-auto">
              Mulai gratis, upgrade kapan saja. Tanpa biaya tersembunyi.
            </p>
          </div>
          {plans.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-10">Memuat paket...</div>
          ) : (
          <>
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm" role="tablist" aria-label="Pilih periode pembayaran">
              <button
                type="button"
                role="tab"
                aria-selected={billingPeriod === 'monthly'}
                onClick={() => setBillingPeriod('monthly')}
                data-testid="billing-toggle-monthly"
                className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors ${billingPeriod === 'monthly' ? 'bg-[#2D6CDF] text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Bulanan
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={billingPeriod === 'yearly'}
                onClick={() => setBillingPeriod('yearly')}
                data-testid="billing-toggle-yearly"
                className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors flex items-center gap-2 ${billingPeriod === 'yearly' ? 'bg-[#2D6CDF] text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
              >
                Tahunan
                {yearlySavings > 0 && (
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${billingPeriod === 'yearly' ? 'bg-white text-[#2D6CDF]' : 'bg-emerald-100 text-emerald-700'}`}>Hemat {yearlySavings}%</span>
                )}
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, i) => (
              <div key={i} className={`relative rounded-2xl border ${plan.badge ? 'border-[#2D6CDF] shadow-lg shadow-blue-500/10 scale-[1.02]' : 'border-slate-200'} bg-white p-8 transition-shadow hover:shadow-lg`}>
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#2D6CDF] text-white px-4 py-1 text-xs font-bold shadow">{plan.badge}</Badge>
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{plan.desc}</p>
                </div>
                <div className="mb-6">
                  <div>
                    <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>{plan.price}</span>
                    <span className="text-sm text-slate-500 ml-1">{plan.period}</span>
                  </div>
                  {plan.subPrice && (
                    <p className="text-xs text-slate-500 mt-1">{plan.subPrice}</p>
                  )}
                </div>
                <Button
                  className={`w-full h-11 text-sm font-semibold mb-6 ${plan.badge ? 'bg-[#2D6CDF] hover:bg-[#1A5BC4] text-white shadow-sm' : ''}`}
                  variant={plan.variant}
                  onClick={() => navigate('/register')}
                >
                  {plan.cta} {plan.badge && <ArrowRight size={16} className="ml-1" />}
                </Button>
                <ul className="space-y-3">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <CheckCircle2 size={16} className={`mt-0.5 flex-shrink-0 ${plan.badge ? 'text-[#2D6CDF]' : 'text-emerald-500'}`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          </>
          )}
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section id="testimonials" className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 px-3 py-1 text-emerald-600 border-emerald-300 font-semibold">Dipercaya Ribuan Pebisnis</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Apa Kata Mereka Tentang Tokomoe
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="border-slate-200 hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} size={16} className="fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed mb-5">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#2D6CDF]/10 flex items-center justify-center text-sm font-bold text-[#2D6CDF]">{t.avatar}</div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LATEST UPDATES ===== */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="outline" className="mb-4 px-3 py-1 text-rose-600 border-rose-300 font-semibold">Terbaru</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Fitur Terbaru yang Kami Rilis
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Terus berkembang dengan pembaruan fitur rutin untuk mendukung pertumbuhan bisnis Anda
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Gift, title: 'Loyalty & Reward System', desc: 'Sistem poin loyalitas otomatis setiap transaksi, katalog hadiah yang bisa di-redeem pelanggan, dan gamifikasi untuk boost repeat order.', color: '#F43F5E', tag: 'Baru' },
              { icon: Landmark, title: 'Transfer Bank Manual', desc: 'Metode pembayaran transfer bank dengan upload bukti bayar, notifikasi otomatis, dan approval langsung dari dashboard tenant.', color: '#0284C7', tag: 'Baru' },
              { icon: UserCheck, title: 'Auto-Fill Checkout', desc: 'Member yang sudah login otomatis terisi data nama, email, telepon, dan alamat di halaman checkout — tanpa perlu input ulang.', color: '#10B981', tag: 'Baru' },
              { icon: Repeat, title: 'Upsell & Downsell Funnel', desc: 'Tawarkan produk upgrade setelah checkout berhasil. Jika ditolak, tampilkan produk downsell untuk maksimalkan konversi.', color: '#7C3AED', tag: 'Pro' },
              { icon: Monitor, title: 'POS Kasir Offline', desc: 'Sistem kasir untuk penjualan offline dengan manajemen shift, struk otomatis, dan sinkronisasi data ke dashboard utama.', color: '#059669', tag: 'Pro' },
              { icon: BrainCircuit, title: 'Advanced Analytics', desc: 'Dashboard analytics mendalam — revenue chart, top products, customer insights, conversion rate, dan laporan keuangan.', color: '#2D6CDF', tag: 'Pro' },
            ].map(({ icon: Icon, title, desc, color, tag }, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="absolute top-4 right-4">
                  <Badge className={`text-[10px] font-bold ${tag === 'Baru' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}`}>{tag}</Badge>
                </div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${color}15` }}>
                  <Icon size={22} style={{ color }} />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRUST BADGES ===== */}
      <section className="py-14 bg-slate-50 border-y border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {[
              { icon: Lock, text: 'SSL Encrypted' },
              { icon: Shield, text: 'Data Protection' },
              { icon: Clock, text: '99.9% Uptime' },
              { icon: Headphones, text: '24/7 Support' },
              { icon: Award, text: 'ISO Certified' },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-2 text-slate-500">
                <Icon size={20} className="text-slate-400" />
                <span className="text-sm font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 px-3 py-1 text-slate-600 border-slate-300 font-semibold">FAQ</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Pertanyaan yang Sering Diajukan
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden bg-white hover:border-slate-300 transition-colors">
                <button
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-sm font-semibold text-slate-900 pr-4">{faq.q}</span>
                  {openFaq === i ? <ChevronUp size={18} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={18} className="text-slate-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #6C5CE7 0%, #2D6CDF 100%)' }} />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {ctaContent.cta_title}
          </h2>
          <p className="text-base md:text-lg text-blue-100 mb-8 max-w-xl mx-auto whitespace-pre-wrap">
            {ctaContent.cta_subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => navigate('/register')} data-testid="bottom-cta-btn" className="bg-white text-[#2D6CDF] hover:bg-blue-50 shadow-lg h-12 px-10 text-base font-semibold">
              {ctaContent.cta_primary_btn} <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button size="lg" variant="ghost" onClick={() => scrollTo('pricing')} className="text-white hover:bg-white/10 h-12 px-8 text-base">
              {ctaContent.cta_secondary_btn} <ArrowUpRight size={16} className="ml-1" />
            </Button>
          </div>
          {ctaContent.cta_features?.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-sm text-blue-200">
              {ctaContent.cta_features.map((f, i) => (
                <span key={i} className="flex items-center gap-1.5"><CheckCircle2 size={14} /> {f}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-16 bg-slate-900 text-slate-400">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="Tokomoe" className="h-8 object-contain brightness-0 invert" />
              </div>
              <p className="text-sm leading-relaxed">Platform SaaS multi-tenant untuk membangun bisnis online lengkap — toko, kursus, membership, dan lainnya.</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Produk</h4>
              <ul className="space-y-2.5 text-sm">
                <li><button onClick={() => scrollTo('features')} className="hover:text-white transition-colors">Fitur</button></li>
                <li><button onClick={() => scrollTo('pricing')} className="hover:text-white transition-colors">Harga</button></li>
                <li><button onClick={() => scrollTo('testimonials')} className="hover:text-white transition-colors">Testimoni</button></li>
                <li><button onClick={() => scrollTo('faq')} className="hover:text-white transition-colors">FAQ</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Bisnis</h4>
              <ul className="space-y-2.5 text-sm">
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">Toko Online</button></li>
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">Kursus & LMS</button></li>
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">Membership</button></li>
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">POS Kasir</button></li>
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">PPOB & Digital</button></li>
                <li><button onClick={() => scrollTo('use-cases')} className="hover:text-white transition-colors">Sales Funnel</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Dukungan</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/help" className="hover:text-white transition-colors">Pusat Bantuan</a></li>
                <li><a href="/api-docs" className="hover:text-white transition-colors">Dokumentasi API</a></li>
                <li><a href="/status" className="hover:text-white transition-colors">Status Server</a></li>
                <li><a href="mailto:support@tokomoe.com" className="flex items-center gap-1.5 hover:text-white transition-colors"><Mail size={14} />support@tokomoe.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm">&copy; {new Date().getFullYear()} Tokomoe. All rights reserved.</p>
            <div className="flex gap-6 text-sm">
              <button onClick={() => scrollTo('faq')} className="hover:text-white transition-colors">Syarat & Ketentuan</button>
              <button onClick={() => scrollTo('faq')} className="hover:text-white transition-colors">Kebijakan Privasi</button>
            </div>
          </div>
        </div>
      </footer>
      <FloatingWhatsApp />
    </div>
  );
}
